import AddIcon from '@mui/icons-material/Add';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckIcon from '@mui/icons-material/Check';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InventoryIcon from '@mui/icons-material/Inventory';
import SettingsIcon from '@mui/icons-material/Settings';
import { alpha, Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import {
  AuditOperation,
  AuditOperationStatusEnum,
  AuditOperationStepTypeEnum,
} from '@store/audits/audits-operation/audits-operation.model';
import { auditsOperationService } from '@store/audits/audits-operation/audits-operation.service';
import { getTypeOf } from '@utils/getTypeOf';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ContainerComponent from './container/Container.component';

const handleTypeTraduction = () => [
  { value: AuditOperationStatusEnum.VALIDATED, label: t('audits.preco.validated') },
  { value: AuditOperationStatusEnum.OPTIMIZED, label: t('audits.preco.optimized') },
  { value: AuditOperationStatusEnum.CREATED, label: t('audits.preco.created') },
];

const determineColorWithOpacity = (value?: string) => {
  switch (value) {
    case AuditOperationStatusEnum.VALIDATED:
      return alpha('#6FD969', 0.14);
    case AuditOperationStatusEnum.OPTIMIZED:
      return alpha('#F2A93B', 0.14);
    case AuditOperationStatusEnum.CREATED:
      return alpha('#4E80EE', 0.14);
  }
};

const determineColor = (value?: string) => {
  switch (value) {
    case AuditOperationStatusEnum.VALIDATED:
      return '#6FD969';
    case AuditOperationStatusEnum.OPTIMIZED:
      return '#F2A93B';
    case AuditOperationStatusEnum.CREATED:
      return '#4E80EE';
  }
};

const handleType = (value?: string) => {
  switch (value) {
    case AuditOperationStatusEnum.VALIDATED:
      return <CheckIcon sx={{ color: '#6FD969', width: '16px', height: '16px' }} />;
    case AuditOperationStatusEnum.OPTIMIZED:
      return <SettingsIcon sx={{ color: '#F2A93B', width: '16px', height: '16px' }} />;
    case AuditOperationStatusEnum.CREATED:
      return <AddIcon sx={{ color: '#4E80EE', width: '16px', height: '16px' }} />;
  }
};

interface PrecoContainerContentProps {
  auditOperation: AuditOperation;
  isTemplate?: boolean;
  isDraggable?: boolean;
}

const PrecoContainerContent = (props: PrecoContainerContentProps) => {
  const { auditOperation, isTemplate = false, isDraggable } = props;
  const [isExpanded, setIsExpended] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const validateAuditOperation = () => {
    if (auditOperation && auditOperation.auditId) {
      auditsOperationService.validateAuditOperation(auditOperation.id as number, auditOperation.auditId as number).subscribe({
        next: () => {
          enqueueSnackbar(t('audits.preco.validationSuccess'), { variant: 'success' });
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };
  return (
    <Stack spacing={2}>
      <Stack direction="row" width="100%" px="15px" py="15px" justifyContent="space-between" borderRadius="4px" bgcolor="#F4F6F8">
        <Stack justifyContent={isExpanded ? 'start' : 'center'} alignItems="start" mr={!status ? '3rem' : '0'} >
          <Stack direction="row" alignItems="center" spacing={1.3} >
            <Stack sx={{
              backgroundColor: '#4F3FF5',
              borderRadius: '3px',
              alignItems: 'center',
              padding: '4px',
              justifyContent: 'center',
              width: '25px',
              height: '25px'
            }}>
              <InventoryIcon sx={{ color: 'white', width: '18px' }} />
            </Stack>
            <Typography fontSize='14px' fontWeight={700} color="#192133" noWrap>
              {`${auditOperation.container?.name} - ${auditOperation.waste?.name}`}
            </Typography>
          </Stack>
        </Stack>
        {(auditOperation.stepType === AuditOperationStepTypeEnum.PRECO || auditOperation.stepType === AuditOperationStepTypeEnum.CONTRACT) && (
          <Stack justifyContent={isExpanded ? 'start' : 'center'} alignItems='start' mr={'1rem'}>
            <AddCircleIcon />
          </Stack>
        )
        }

        <Stack width="130px" height='100%' mr='3rem' justifyContent={isExpanded ? 'start' : 'center'} alignItems='start'>
          <Stack direction="row" p='6px 11px' bgcolor={determineColorWithOpacity(auditOperation.status)} spacing={1} >
            {handleType(auditOperation.status)}
            <Typography fontSize='12px' fontWeight={400} color={determineColor(auditOperation.status)}>
              {handleTypeTraduction().find((item) => item.value === auditOperation.status)?.label}
            </Typography>
          </Stack>
        </Stack>

        <Stack width="100%">
          <ContainerComponent isExpended={isExpanded} auditOperation={auditOperation} oldValue={getTypeOf<AuditOperation>(auditOperation.oldValue)} />
        </Stack>
        <Stack direction="row" justifyContent='end' alignItems={isExpanded ? 'start' : 'center'} spacing={2} ml='1rem'>
          {!isDraggable ? (
            <IconButton sx={{ color: '#03053A' }}>
              <DragHandleIcon />
            </IconButton>
          ) : (
            <IconButton onClick={() => setIsExpended(!isExpanded)}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Stack>
      </Stack >
      {isTemplate || auditOperation.status !== AuditOperationStatusEnum.VALIDATED && (
        <Stack direction="row" spacing={2} alignSelf="end">
          <Button variant="contained" color="primary" onClick={() => {
            navigate('optimization-container', { state: { curentAuditOperation: auditOperation } });
          }}>
            {t('audits.buttons.optimisation')}
          </Button>
          <Divider orientation="vertical" />
          <Button variant="outlined" color="primary" onClick={() => validateAuditOperation()}>
            {t('global.validate')}
          </Button>
        </Stack>
      )
      }
    </Stack >
  );
};

export default PrecoContainerContent;
